import { createContext, useContext, useLayoutEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

/**
 * Acento ONOR — identidad v2.1 "Industrial Steel": bronce fijo. El selector de acentos quedó anulado por decisión de marca (oct 2026).
 *
 * Reglas de colorimetría / MKT aplicadas:
 *  - Todos son colores vibrantes y energéticos (coherentes con el ADN atlético
 *    de ONOR); el naranja es la identidad principal y va por default.
 *  - Luminosidad L acotada (≈34–59%) para que el texto BLANCO de los botones
 *    (bold + tamaño grande) mantenga contraste legible (WCAG AA para texto
 *    grande ≥ 3:1).
 *  - Saturación alta y uniforme → sensación premium y consistente entre temas.
 *  - Se guardan como HSL (para --primary/--ring) y rgb (para el glow).
 */
export const ONOR_THEMES = [
  { id: 'bronze', label: 'Bronce ONOR', desc: 'Identidad oficial v2.1', hsl: '22 37% 44%', hex: '#9A6647', glow: '154,102,71' },
];

const DEFAULT_ID = 'bronze';

const ThemeContext = createContext(null);

const keyFor = (user) => `onor_accent_${user?.id || user?.email || 'guest'}`;

/** Aplica el tema escribiendo las variables CSS en :root. */
const applyTheme = (theme) => {
  if (!theme || typeof document === 'undefined') return;
  const r = document.documentElement;
  r.style.setProperty('--primary', theme.hsl);
  r.style.setProperty('--ring', theme.hsl);
  // Glows/sombras de marca — todas derivan del mismo tono para mantener
  // coherencia colorimétrica y atenuaciones estéticas al cambiar de acento.
  r.style.setProperty('--glow-primary', `0 0 24px -4px rgba(${theme.glow}, 0.35)`);
  r.style.setProperty('--glow-primary-strong', `0 0 24px 0 rgba(${theme.glow}, 0.55)`);
  r.style.setProperty('--shadow-card-hover', `0 12px 40px -12px rgba(${theme.glow}, 0.20)`);
  r.style.setProperty('--accent-orange', theme.hex);
};

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const [themeId, setThemeId] = useState(DEFAULT_ID);

  // Resolver + aplicar el acento de forma SÍNCRONA cada vez que cambia el
  // usuario (login/logout). Usamos useLayoutEffect para que el reset ocurra
  // ANTES del paint: así, al cerrar sesión, el landing público nunca alcanza a
  // pintarse con el acento del usuario anterior (default ONOR Naranja para
  // invitados). Sin esto, el reset diferido de useEffect dejaba un flash del
  // color previo filtrándose a las páginas de marca.
  useLayoutEffect(() => {
    let saved = null;
    try { saved = localStorage.getItem(keyFor(user)); } catch { /* no-op */ }
    const id = ONOR_THEMES.some((t) => t.id === saved) ? saved : DEFAULT_ID;
    setThemeId(id);
    applyTheme(ONOR_THEMES.find((t) => t.id === id) || ONOR_THEMES[0]);
  }, [user]);

  // Aplicar el tema cuando el usuario lo cambia manualmente en Apariencia.
  useLayoutEffect(() => {
    const theme = ONOR_THEMES.find((t) => t.id === themeId) || ONOR_THEMES[0];
    applyTheme(theme);
  }, [themeId]);

  const setTheme = useCallback((id) => {
    if (!ONOR_THEMES.some((t) => t.id === id)) return;
    setThemeId(id);
    try { localStorage.setItem(keyFor(user), id); } catch { /* no-op */ }
  }, [user]);

  const resetTheme = useCallback(() => setTheme(DEFAULT_ID), [setTheme]);

  return (
    <ThemeContext.Provider value={{ themeId, setTheme, resetTheme, themes: ONOR_THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme debe usarse dentro de <ThemeProvider>');
  return ctx;
};
